import type { Message } from 'ai';
import { useSWRConfig } from 'swr';
import { useCopyToClipboard } from 'usehooks-ts';

import type { Vote } from '@/lib/api-client';
import { apiClient } from '@/lib/api-client';
import { API_BASE_URL } from '@/lib/config';
import { useAuth } from '@/contexts/AuthContext';
import { telemetry } from '@/lib/telemetry';

import { CopyIcon, ThumbDownIcon, ThumbUpIcon } from './icons';
import { Button } from './ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import { memo } from 'react';
import equal from 'fast-deep-equal';
import { toast } from 'sonner';
import { GitFork, Sparkles } from 'lucide-react';
import { useRouter } from 'next/navigation';

export function PureMessageActions({
  chatId,
  message,
  vote,
  isLoading,
  onSaveAsSkill,
  messageIndex,
}: {
  chatId: string;
  message: Message;
  vote: Vote | undefined;
  isLoading: boolean;
  /** Turn the current chat into a reusable skill (arms create-a-skill in the
   *  live chat). Only rendered when provided (normal chat, not readonly). */
  onSaveAsSkill?: () => void;
  /** Index of this message in the conversation (used for fork-up-to-point). */
  messageIndex?: number;
}) {
  const { mutate } = useSWRConfig();
  const [_, copyToClipboard] = useCopyToClipboard();
  const { user: firebaseUser } = useAuth();
  const router = useRouter();

  if (isLoading) return null;
  if (message.role === 'user') return null;

  return (
    <TooltipProvider delayDuration={0}>
      <div className="flex flex-row gap-2">
        {onSaveAsSkill && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                data-testid="message-save-as-skill"
                className="py-1 px-2 h-fit gap-1.5 border-emerald-400/30 bg-emerald-500/[0.06] text-emerald-300 hover:bg-emerald-500/15 hover:text-emerald-200"
                variant="outline"
                onClick={() => onSaveAsSkill()}
              >
                <Sparkles size={14} />
                <span className="text-xs font-medium">Save as skill</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>Turn this chat into a reusable skill</TooltipContent>
          </Tooltip>
        )}

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              data-testid="message-fork-chat"
              className="py-1 px-2 h-fit gap-1.5 border-white/10 bg-white/[0.03] text-white/80 hover:bg-white/[0.08] hover:text-white"
              variant="outline"
              onClick={() => {
                if (!firebaseUser?.uid) {
                  toast.error('Please log in to fork a chat');
                  return;
                }

                telemetry.emit('session.fork_click', {
                  session_id: chatId,
                  user_id: firebaseUser.uid,
                  source: 'message_actions',
                  message_index: messageIndex,
                });

                const forkPromise = fetch(
                  `${API_BASE_URL}/sessions/${chatId}/fork?requesting_user_id=${encodeURIComponent(
                    firebaseUser.uid,
                  )}${
                    messageIndex !== undefined ? `&up_to_message_index=${messageIndex}` : ''
                  }`,
                  { method: 'POST' },
                ).then(async (response) => {
                  if (!response.ok) {
                    const text = await response.text().catch(() => '');
                    let errorMessage = `Fork failed (${response.status})`;
                    try {
                      const body = JSON.parse(text);
                      if (body?.detail) errorMessage = body.detail;
                    } catch {
                      if (text) errorMessage = text.slice(0, 300);
                    }
                    throw new Error(errorMessage);
                  }
                  return response.json();
                });

                toast.promise(forkPromise, {
                  loading: 'Forking chat...',
                  success: (data: any) => {
                    const newSessionId = data?.session_id;
                    telemetry.emit('session.fork_success', {
                      session_id: chatId,
                      user_id: firebaseUser.uid,
                      new_session_id: newSessionId,
                      message_count: data?.message_count,
                      source: 'message_actions',
                      message_index: messageIndex,
                    });
                    if (newSessionId) {
                      router.push(`/chat/${newSessionId}`);
                    }
                    return 'Chat forked';
                  },
                  error: (err) => {
                    telemetry.emit('session.fork_error', {
                      session_id: chatId,
                      user_id: firebaseUser.uid,
                      error_message:
                        err instanceof Error
                          ? err.message.slice(0, 500)
                          : String(err).slice(0, 500),
                      source: 'message_actions',
                      message_index: messageIndex,
                    });
                    return err instanceof Error && err.message
                      ? err.message
                      : 'Failed to fork chat';
                  },
                });
              }}
            >
              <GitFork size={14} />
              <span className="text-xs font-medium">Fork chat</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>Fork chat up to this point</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              className="py-1 px-2 h-fit text-muted-foreground"
              variant="outline"
              onClick={async () => {
                const textFromParts = message.parts
                  ?.filter((part) => part.type === 'text')
                  .map((part) => part.text)
                  .join('\n')
                  .trim();

                if (!textFromParts) {
                  toast.error("There's no text to copy!");
                  return;
                }

                await copyToClipboard(textFromParts);
                toast.success('Copied to clipboard!');
              }}
            >
              <CopyIcon />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Copy</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              data-testid="message-upvote"
              className="py-1 px-2 h-fit text-muted-foreground !pointer-events-auto"
              disabled={vote?.isUpvoted}
              variant="outline"
              onClick={async () => {
                const upvote = apiClient.voteMessage(chatId, message.id, 'up');

                toast.promise(upvote, {
                  loading: 'Upvoting Response...',
                  success: () => {
                    mutate<Array<Vote>>(
                      `votes-${chatId}`,
                      (currentVotes) => {
                        if (!currentVotes) return [];

                        const votesWithoutCurrent = currentVotes.filter(
                          (vote) => vote.messageId !== message.id,
                        );

                        return [
                          ...votesWithoutCurrent,
                          {
                            chatId,
                            messageId: message.id,
                            isUpvoted: true,
                          },
                        ];
                      },
                      { revalidate: false },
                    );

                    return 'Upvoted Response!';
                  },
                  error: 'Failed to upvote response.',
                });
              }}
            >
              <ThumbUpIcon />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Upvote Response</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              data-testid="message-downvote"
              className="py-1 px-2 h-fit text-muted-foreground !pointer-events-auto"
              variant="outline"
              disabled={vote && !vote.isUpvoted}
              onClick={async () => {
                const downvote = apiClient.voteMessage(chatId, message.id, 'down');

                toast.promise(downvote, {
                  loading: 'Downvoting Response...',
                  success: () => {
                    mutate<Array<Vote>>(
                      `votes-${chatId}`,
                      (currentVotes) => {
                        if (!currentVotes) return [];

                        const votesWithoutCurrent = currentVotes.filter(
                          (vote) => vote.messageId !== message.id,
                        );

                        return [
                          ...votesWithoutCurrent,
                          {
                            chatId,
                            messageId: message.id,
                            isUpvoted: false,
                          },
                        ];
                      },
                      { revalidate: false },
                    );

                    return 'Downvoted Response!';
                  },
                  error: 'Failed to downvote response.',
                });
              }}
            >
              <ThumbDownIcon />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Downvote Response</TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
}

export const MessageActions = memo(
  PureMessageActions,
  (prevProps, nextProps) => {
    if (!equal(prevProps.vote, nextProps.vote)) return false;
    if (prevProps.isLoading !== nextProps.isLoading) return false;

    return true;
  },
);
